package scan

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video
import org.w3c.dom.HTMLVideoElement
import org.w3c.dom.events.Event
import puzzlekey.Box
import puzzlekey.guideOnScreen

/**
 * The camera under the guide overlay (ADR-0004 §2), with a tap-to-start button ([startLabel]) while
 * [cameraOn] is false. [onVideo] hands over the `<video>` the Scan reads; [onStarted] runs once the camera is live;
 * [onError] gets what [startCamera] threw.
 * [overlay] (the Radar's sparkles) covers the stage over the guide while the camera is on.
 */
@Composable
fun CameraStage(
    cameraOn: Boolean,
    onVideo: (HTMLVideoElement) -> Unit,
    onStarted: () -> Unit,
    onError: (Throwable) -> Unit,
    startLabel: String = "Tap to start the camera",
    overlay: @Composable () -> Unit = {},
) {
    var video by remember { mutableStateOf<HTMLVideoElement?>(null) }
    var guide by remember { mutableStateOf<Box?>(null) }
    val scope = rememberCoroutineScope()

    fun layoutGuide() {
        val v = video ?: return
        guide = guideOnScreen(v.clientWidth.toDouble(), v.clientHeight.toDouble())
    }

    DisposableEffect(video) {
        val onResize: (Event) -> Unit = { layoutGuide() }
        window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }

    Div({ classes("stage") }) {
        Video({
            attr("playsinline", "")
            attr("muted", "")
            attr("autoplay", "")
            ref { element ->
                element.muted = true
                element.onloadedmetadata = { layoutGuide() }
                video = element
                onVideo(element)
                onDispose { stopCamera(element) }
            }
        })
        guide?.takeIf { cameraOn }?.let { g ->
            Div({
                classes("guide")
                style {
                    property("left", "${g.x}px")
                    property("top", "${g.y}px")
                    property("width", "${g.w}px")
                    property("height", "${g.h}px")
                }
            })
        }
        if (cameraOn) overlay()
        if (!cameraOn) {
            Button({
                classes("start")
                onClick {
                    val v = video ?: return@onClick
                    scope.launch {
                        try {
                            startCamera(v)
                            layoutGuide()
                            onStarted()
                        } catch (e: Throwable) {
                            onError(e)
                        }
                    }
                }
            }) { Text(startLabel) }
        }
    }
}
